import { useEffect, useMemo } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { UseFormMethods } from 'react-hook-form'

import {
  useManualIncidentCategoriesQuery,
  useRecipientsByRuleQuery,
  useRuleDevicesQuery,
  useRuleQuery,
} from '@/graphql/generated/hooks'
import { Action, DeviceType, RepeatInterval } from '@/graphql/generated/schemas'

import { FormInputsT, RuleInputDataIProps } from '../types/types'
import {
  getActionOption,
  getDateWithoutLocalTZOffset,
  getDeviceOptions,
  getEventTypeOptions,
  getFloorsOptions,
  getOption,
  getRecipientOptions,
  getRuleDeviceIds,
  getSelectedDays,
  getTimeDate,
  getTimeInTimeZone,
} from '../utils/utils'

export type UseRuleIProps = {
  id: string
  form: UseFormMethods<FormInputsT>
}
export const useRule = ({ id, form }: UseRuleIProps): RuleInputDataIProps => {
  const { setValue } = form

  const { data, loading: isLoading } = useRuleQuery({
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only',
  })

  const { data: deviceData, loading: isDeviceLoading } = useRuleDevicesQuery({
    variables: { ids: getRuleDeviceIds(data?.rule?.ruleLogics) },
    skip: !data?.rule?.ruleLogics,
  })

  const { data: recipientData, loading: isRecipientsLoading } =
    useRecipientsByRuleQuery({
      variables: { filter: { ruleId: id } },
      skip: !id,
      fetchPolicy: 'network-only',
    })

  const { data: categoriesData } = useManualIncidentCategoriesQuery()
  const facility = deviceData?.devices?.edges?.[0]?.node?.facility
  const isDevelopmentModeEnabled = useFeatureFlag(FeatureFlag.development)

  useEffect(() => {
    if (data && !isLoading) {
      setValue('name', data?.rule?.name)
      setValue('action', getActionOption(data?.rule?.action))
      setValue(
        'event',
        getEventTypeOptions(
          data?.rule?.ruleEventType,
          data?.rule?.manualIncidentCategoryIds,
          categoriesData
        )
      )
    }
  }, [isLoading, data, categoriesData, setValue])

  useEffect(() => {
    if (data && !isLoading) {
      setValue('facility', getOption(facility))
      setValue('floor', getFloorsOptions(deviceData))
      setValue('cameras', getDeviceOptions(deviceData)?.cameraOptions)
      setValue('doors', getDeviceOptions(deviceData)?.doorOptions)
      setValue('alarms', getDeviceOptions(deviceData)?.alarmOptions)
    }
  }, [isDeviceLoading, deviceData, data, isLoading, facility, setValue])

  // Fixed: Only set email recipients when both main rule data AND recipient data are loaded
  useEffect(() => {
    if (recipientData && !isRecipientsLoading && data && !isLoading) {
      setValue('emails', getRecipientOptions(recipientData))
    }
  }, [isRecipientsLoading, recipientData, data, isLoading, setValue])

  const timeZone = facility?.timeZone ?? 'utc'
  const facilityName = facility?.name
  const endTimeDate = getTimeDate(
    data?.rule?.endTimeOfDay,
    data?.rule?.startTime
  )
  const emailOptions = useMemo(
    () => getRecipientOptions(recipientData),
    [recipientData]
  )

  const doorIds = useMemo(
    () =>
      deviceData?.devices?.edges
        ?.filter((e) => e.node?.type === DeviceType.Door)
        ?.map((f) => f?.node?.id) || [],
    [deviceData]
  )

  // Determine if we should show recipients based on action AND if main rule data is loaded
  const shouldShowRecipients =
    data?.rule?.action === Action.SendEmail && !isLoading

  // Enhanced loading state that considers both main rule loading and recipients loading
  // This prevents the race condition by ensuring recipients don't show until all data is ready
  const isRecipientsLoadingEnhanced =
    isRecipientsLoading || (shouldShowRecipients && isLoading)

  return {
    id: id,
    days: getSelectedDays(data?.rule?.day),
    doorIds,
    emailOptions,
    endTimeDate: getTimeInTimeZone(endTimeDate, timeZone),
    endDate: data?.rule?.endDate
      ? getDateWithoutLocalTZOffset(data?.rule?.endDate)
      : null,
    facilityName,
    name: data?.rule?.name,
    startTime: getTimeInTimeZone(data?.rule?.startTime, timeZone),
    repeatIntervalNum: data?.rule?.repeatIntervalNum || 0,
    shouldRepeat: Boolean(data?.rule?.repeatInterval),
    shouldEnd: Boolean(data?.rule?.endDate),
    shouldShowDays: data?.rule?.repeatInterval === RepeatInterval.Weekly,
    shouldShowRecipients,
    aiOperatorResponseRules: data?.rule?.aiOperatorResponseRules ?? [],
    timeZone,
    isLoading,
    isDeviceLoading,
    // Use enhanced loading state to prevent race condition
    isRecipientsLoading: isRecipientsLoadingEnhanced,
    isDevelopmentModeEnabled,
    allDayOverride: data?.rule?.allDayOverride,
    isDryRun: data?.rule?.aiOperatorRuleDetails?.isDryRun ?? false,
  }
}
